import { PageHeader } from '@ant-design/pro-layout';
import { t } from '@hefe/i18n';
import {
  BasketballEventType,
  BasketballTeamPlayerReplies,
  UserBasic,
} from '@hefe/models';
import { BasketballPracticesSlice, ModalsSlice } from '@hefe/store';
import { dateFormat, timeFormat } from '@hefe/utils';
import { Avatar, Card, Col, Row, Space, Spin, Tabs, Typography } from 'antd';
import { Gutter } from 'antd/lib/grid/row';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { AiOutlineReload } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import UserAvatar from '../../../core/components/user/user-avatar/user-avatar/user-avatar.component';
import EventDetailsAuditsComponent from '../event-details/event-details-audits/event-details-audits.component';
import EventDetailsCommentsComponent from '../event-details/event-details-comments/event-details-comments.component';
import PracticeDetailsClubComponent from './practice-details-club/practice-details-club.component';
import PracticeDetailsSameDayPracticesComponent from './practice-details-same-day-practices/practice-details-same-day-practices.component';
import PracticeDetailsTeamComponent from './practice-details-team/practice-details-team.component';

export default function PracticeDetailsComponent() {
  const dispatch = useAppDispatch();
  const practiceState = useSelector(
    BasketballPracticesSlice.basketballPracticesState
  );
  const practiceModal = useSelector(ModalsSlice.modalsState).practiceModal;
  const practiceId = practiceModal.id;

  const practice = practiceState.practiceDetails[practiceId];

  useEffect(() => {
    if (practiceId && practiceId > 0 && !practice) {
      fetchPractice();
    }
  }, [practiceId, practice]);

  const fetchPractice = () => {
    dispatch(BasketballPracticesSlice.fetchBasketballPractice(practiceId));
  };

  const gutter = [16, 16] as [Gutter, Gutter];

  let userPlayers: UserBasic[] = [];
  let userCoaches: UserBasic[] = [];
  const replies: BasketballTeamPlayerReplies = {
    yes: [],
    no: [],
    unknown: [],
    not_nominated: [],
  };

  practice?.teams?.forEach((team) => {
    let isCoach = false;
    if (team.user_players) userPlayers = [...userPlayers, ...team.user_players];
    if (team.user_coaches) {
      userCoaches = [...userCoaches, ...team.user_coaches];
      isCoach = team.user_coaches.length > 0;
    }

    if (team.playerReplies && (team.allowViewReplies || isCoach)) {
      replies.yes = [...replies.yes, ...team.playerReplies.yes];
      replies.no = [...replies.no, ...team.playerReplies.no];
      replies.unknown = [...replies.unknown, ...team.playerReplies.unknown];
    }
  });

  // combine userplayers and usercoaches and Remove duplicates by id
  const userCoachesAndPlayers = [...userPlayers, ...userCoaches].reduce(
    (acc: UserBasic[], current: UserBasic) => {
      const x = acc.find((item) => item.id === current.id);
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    },
    []
  );

  const leftSideTabs = useMemo(() => {
    if (!practice) {
      return [];
    }

    const tabs = [];

    tabs.push({
      key: 'comments',
      label: t('comments.plural'),
      children: (
        <EventDetailsCommentsComponent
          eventId={practice.id}
          eventType={BasketballEventType.PRACTICE}
          teamId={practice.team_id}
          users={userCoachesAndPlayers}
          comments={practice.comments}
        />
      ),
    });

    if (practice?.other_practices?.length > 0) {
      tabs.push({
        key: 'other_practices',
        label: t('practices.other_practices.same_day.title'),
        children: (
          <PracticeDetailsSameDayPracticesComponent
            practices={practice?.other_practices}
          />
        ),
      });
    }

    return tabs;
  }, [practice, userCoachesAndPlayers]);

  if (practiceState.loadingPracticeDetails) {
    return <Spin style={{ textAlign: 'center' }} size="large" />;
  }

  if (!practice) {
    return null;
  }

  const defaultTeamKey =
    practice.teams?.length > 0 ? `${practice.teams[0].id}` : '1';
  const defaultActiveKey =
    practice?.club_tasks && practice?.club_tasks?.length > 0
      ? 'club'
      : defaultTeamKey;

  return (
    <Row gutter={gutter}>
      <Col xs={24} lg={10} xxl={8}>
        <Row gutter={gutter}>
          <Col xs={24}>
            <Card>
              <PageHeader
                title="Training"
                extra={[
                  <AiOutlineReload
                    size={20}
                    style={{ cursor: 'pointer' }}
                    onClick={() => fetchPractice()}
                  />,
                ]}
              >
                <Space direction="vertical" size={0}>
                  <Typography.Text>
                    {dayjs(practice?.date).format(
                      'ddd ' + dateFormat() + ' - ' + timeFormat()
                    )}
                  </Typography.Text>
                  <Typography.Text>{practice?.location}</Typography.Text>
                  <Avatar.Group style={{ marginTop: 10 }}>
                    {userCoachesAndPlayers.map((user) => {
                      return (
                        <UserAvatar
                          key={user.id}
                          user={user}
                          size="default"
                          withTooltip
                        />
                      );
                    })}
                  </Avatar.Group>
                </Space>
              </PageHeader>
            </Card>
          </Col>
          <Col xs={24}>
            <Card>
              <Tabs defaultActiveKey="comments" items={leftSideTabs} />
            </Card>
          </Col>
        </Row>
      </Col>
      <Col xs={24} lg={14} xxl={16}>
        <Card>
          <Tabs defaultActiveKey={defaultActiveKey}>
            {practice.teams.map((team) => {
              return (
                <Tabs.TabPane key={team.id} tab={team.name}>
                  <PracticeDetailsTeamComponent
                    practice={practice}
                    team={team}
                    userCoaches={userCoaches}
                  />
                </Tabs.TabPane>
              );
            })}
            {practice?.club_tasks && practice?.club_tasks?.length > 0 && (
              <Tabs.TabPane key={'club'} tab={t('tasks.club')}>
                <PracticeDetailsClubComponent practice={practice} />
              </Tabs.TabPane>
            )}
            <Tabs.TabPane key={3} tab={t('changelog')}>
              <EventDetailsAuditsComponent audits={practice.audits} />
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </Col>
    </Row>
  );
}
